import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';

// Services
import courseService from '../services/courseService';
import enrollmentService from '../services/enrollmentService';
import paymentService from '../services/paymentService';
import playlistService from '../services/playlistService';
import videoService from '../services/videoService';

// Components
import ProtectedVideoPlayer from '../components/ProtectedVideoPlayer'; // --- NEW IMPORT ---

// MUI Icons
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LockIcon from '@mui/icons-material/Lock';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';

const CourseDetailPage = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();

    const [course, setCourse] = useState(null);
    const [playlists, setPlaylists] = useState([]);
    const [enrollment, setEnrollment] = useState(null); 
    const [loading, setLoading] = useState(true);
    const [enrolling, setEnrolling] = useState(false);
    const [error, setError] = useState('');
    
    const [playingDemoVideo, setPlayingDemoVideo] = useState(null);
    const [demoVideoUrl, setDemoVideoUrl] = useState(null);
    const [demoSignatureParams, setDemoSignatureParams] = useState(null); // --- NEW STATE ---

    useEffect(() => {
        const fetchCourseData = async () => {
            try {
                setLoading(true);
                setError('');
                
                // 1. Fetch Course & Playlists
                const [courseData, playlistsData] = await Promise.all([
                    courseService.getCourseById(courseId),
                    playlistService.getPlaylistsByCourseId(courseId),
                ]);

                setCourse(courseData);
                setPlaylists(playlistsData);

                // 2. Fetch Enrollment Details if logged in
                if (isAuthenticated) {
                    try {
                        const myEnrollments = await enrollmentService.getMyEnrollments();
                        const currentEnrollment = myEnrollments.find(e => e.course.id === parseInt(courseId));
                        setEnrollment(currentEnrollment || null);
                    } catch (e) {
                        console.error("Error fetching enrollment:", e);
                    }
                }
            } catch (err) {
                setError('Failed to load course details.');
            } finally {
                setLoading(false);
            }
        };

        fetchCourseData();
    }, [courseId, isAuthenticated]);

    const handleEnroll = async () => {
        if (!isAuthenticated) {
            navigate('/login', { state: { from: `/courses/${courseId}` } });
            return;
        }

        try {
            setEnrolling(true);
            setError('');
            // Proceed with payment
            const result = await paymentService.processPayment(course, user);
            
            if (result.success) {
                // Refresh data to get new enrollment status
                const myEnrollments = await enrollmentService.getMyEnrollments();
                const currentEnrollment = myEnrollments.find(e => e.course.id === parseInt(courseId));
                setEnrollment(currentEnrollment);
                navigate(`/learn/${courseId}`);
            } else {
                setError(result.message || 'Payment verification failed.');
            }
        } catch (err) {
            setError(err.message || 'An error occurred during enrollment.');
        } finally {
            setEnrolling(false);
        }
    };

    const handlePlayDemo = async (videoId) => {
        try {
            const streamData = await videoService.getVideoStreamUrl(videoId);
            setDemoVideoUrl(streamData.data.streamUrl);
            setDemoSignatureParams(streamData.data.signatureParams || null); // --- CAPTURE SIGNATURE ---
            setPlayingDemoVideo(videoId);
        } catch (error) {
            alert('Failed to load demo video');
        }
    };

    const closeDemoPlayer = () => {
        setPlayingDemoVideo(null);
        setDemoVideoUrl(null);
        setDemoSignatureParams(null); // --- CLEAR SIGNATURE ---
    };

    // Derived States
    const isEnrolled = !!enrollment;
    const isExpired = enrollment?.status === 'EXPIRED';
    const isActive = enrollment?.status === 'ACTIVE';

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-white">
                <div className="text-center">
                    <div className="w-10 h-10 border-3 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-slate-500 text-sm">Loading course details...</p>
                </div>
            </div>
        );
    }

    if (error || !course) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center px-4">
                 <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg max-w-md">
                    <p className="font-semibold mb-2">Error</p>
                    <p className="text-sm">{error || 'Course not found.'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-50 border-b border-slate-100">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
                    <div className="max-w-3xl">
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-4 tracking-tight">
                            {course.title}
                        </h1>
                        <p className="text-lg text-slate-600 leading-relaxed">{course.description}</p>
                    </div>
                </div>
            </section>

            <section className="py-12 lg:py-16">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* LEFT COLUMN: Content */}
                        <div className="lg:col-span-2 space-y-6">
                            
                            {/* --- Enrollment Status Banners --- */}
                            {isActive && (
                                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-5">
                                    <div className="flex items-center gap-3 text-emerald-800">
                                        <CheckCircleIcon />
                                        <div>
                                            <p className="font-bold">You are enrolled!</p>
                                            <p className="text-sm opacity-90">
                                                Access expires on: {new Date(enrollment.expiryDate).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isExpired && (
                                <div className="bg-red-50 border border-red-100 rounded-xl p-5">
                                    <div className="flex items-center gap-3 text-red-800">
                                        <WarningIcon />
                                        <div>
                                            <p className="font-bold">Enrollment Expired</p>
                                            <p className="text-sm opacity-90">
                                                Your access expired on {new Date(enrollment.expiryDate).toLocaleDateString()}.
                                                Please enroll again to continue learning.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* -------------------------------------- */}

                            <h2 className="text-2xl font-heading font-bold text-slate-900">Course Content</h2>

                            {playlists.length > 0 && (
                                <div className="space-y-4">
                                    {playlists.map((playlist) => (
                                        <div key={playlist.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                                            <details open>
                                                <summary className="px-6 py-4 font-semibold cursor-pointer hover:bg-slate-50 flex justify-between items-center transition-colors">
                                                    <span className="text-slate-900">
                                                        {playlist.name} ({playlist.videos?.length || 0} videos)
                                                    </span>
                                                    <KeyboardArrowDownIcon className="text-slate-500" />
                                                </summary>
                                                {playlist.description && (
                                                    <div className="px-6 py-2 text-sm text-slate-600 border-b border-slate-100">
                                                        {playlist.description}
                                                    </div>
                                                )}
                                                <div className="px-6 pb-4 space-y-1">
                                                    {playlist.videos?.map((video, index) => (
                                                        <div key={video.id} className="flex items-center py-3 text-slate-700 text-sm border-t border-slate-100 first:border-t-0">
                                                            <span className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-600 mr-3 flex-shrink-0">
                                                                {index + 1}
                                                            </span>
                                                            <span className="flex-1">{video.title}</span>
                                                            {video.isFree ? (
                                                                <button
                                                                    onClick={() => handlePlayDemo(video.id)}
                                                                    className="ml-3 px-3 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 transition-colors flex items-center gap-1"
                                                                >
                                                                    <PlayArrowIcon sx={{ fontSize: 16 }} /> Preview
                                                                </button>
                                                            ) : (
                                                                <span className="ml-3 px-3 py-1 text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded flex items-center gap-1">
                                                                    <LockIcon sx={{ fontSize: 14 }} /> Locked
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </details>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* RIGHT COLUMN: Sidebar */}
                        <div className="lg:col-span-1">
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="sticky top-6">
                                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                    <img src={course.thumbnailUrl || 'https://placehold.co/400x225/1e293b/ffffff?text=Course'} alt={course.title} className="w-full h-auto object-cover" />
                                    <div className="p-6">
                                        <div className="flex items-center gap-3 mb-6">
                                            {/* Show price if not actively enrolled */}
                                            {(!isActive) ? (
                                                <>
                                                    <p className="text-3xl font-bold text-slate-900">₹{course.price}</p>
                                                    {course.originalPrice && <p className="text-lg text-slate-500 line-through">₹{course.originalPrice}</p>}
                                                </>
                                            ) : (
                                                <p className="text-2xl font-bold text-emerald-600">Enrolled</p>
                                            )}
                                        </div>

                                        {/* Action Buttons */}
                                        {isActive ? (
                                            <button onClick={() => navigate(`/learn/${courseId}`)} className="w-full inline-flex items-center justify-center px-7 py-3.5 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-all duration-200 shadow-sm hover:shadow-md gap-2">
                                                <PlayArrowIcon /> Go to Course
                                            </button>
                                        ) : (
                                            <button 
                                                onClick={handleEnroll} 
                                                disabled={enrolling} 
                                                className={`w-full inline-flex items-center justify-center px-7 py-3.5 text-sm font-medium text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed gap-2 ${isExpired ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-900 hover:bg-slate-800'}`}
                                            >
                                                {enrolling ? (
                                                     <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div> Processing...</>
                                                ) : (
                                                    <>
                                                        <ShoppingCartIcon /> 
                                                        {isExpired ? 'Enroll Again' : 'Enroll Now'}
                                                    </>
                                                )}
                                            </button>
                                        )}

                                        <div className="mt-6 pt-6 border-t border-slate-200 space-y-4 text-sm text-slate-600">
                                            <div className="flex items-center gap-2">
                                               <OndemandVideoIcon sx={{ fontSize: 20 }} />
                                               <span>{playlists.reduce((acc, p) => acc + (p.videos?.length || 0), 0)} On-demand videos</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <AccessTimeIcon sx={{ fontSize: 20 }} />
                                                <span>Self-paced learning</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <CalendarTodayIcon sx={{ fontSize: 20 }} />
                                                <span>6 Months Validity</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Demo Player Modal */}
            {playingDemoVideo && demoVideoUrl && (
                <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4" onClick={closeDemoPlayer}>
                    <div className="bg-white rounded-lg max-w-4xl w-full p-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Demo Video</h3>
                            <button onClick={closeDemoPlayer} className="text-slate-500 hover:text-slate-700">
                                <CloseIcon />
                            </button>
                        </div>
                        
                        {/* --- NEW: ProtectedVideoPlayer Implementation --- */}
                        <div className="w-full aspect-video bg-black rounded overflow-hidden">
                            <ProtectedVideoPlayer 
                                streamUrl={demoVideoUrl} 
                                signatureParams={demoSignatureParams}
                            />
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default CourseDetailPage;